import gql from 'graphql-tag';
import React from 'react';
import { Query } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { Button } from '../../../components/Bootstrap';
import { transformAssignmentResponse } from '@getnoble/noble-tip-splitting-utils';
import { Navigation, LayoutContent, Layout } from '../../../components';
import { DeliveryLayoutRow, DeliverySeparator } from '../components';
import { serverAssignmentsFragment } from '../../../fragments';
import { CurrentAssignmentsQuery, CurrentAssignmentsQueryVariables } from './__generated__/CurrentAssignmentsQuery';
import './CurrentAssignments.scss';

interface CurrentAssignmentsRouterParams {
  venueId: string;
  fulfillmentCenterId: string;
  serverId: string;
}

type RouterProps = RouteComponentProps<CurrentAssignmentsRouterParams>;

interface CurrentAssignmentsProps extends RouterProps {
  data?: CurrentAssignmentsQuery;
  loading: boolean;
  error?: Error;
  venueId: string;
  fulfillmentCenterId: string;
}

class CurrentAssignmentsUnwrapped extends React.Component<CurrentAssignmentsProps> {
  renderAssignments() {
    const transformedData = transformAssignmentResponse(this.props.data.serverAssignments.assignments);
    if (transformedData.length === 0) {
      return <p>No assignments</p>;
    } else {
      return transformedData.map((row) => (
        <DeliveryLayoutRow
          parentNames={row.parentNames}
          locations={row.locations}
          fulfillmentCenterName={this.props.data.fulfillmentCenter.name}
          key={row.id}
          showHelpText={false}
        />
      ));
    }
  }

  renderContent() {
    if (this.props.loading || !this.props.data) {
      return <p>Loading...</p>;
    } else {
      const { venueId, fulfillmentCenterId, data } = this.props;
      const editUrl = `/venue/${venueId}/fulfillment-centers/${fulfillmentCenterId}/assignments/${data.serverAssignments.server.id}/edit`;

      return (
        <div className='delivery-assignments'>
          <div>
            <Button href={editUrl} color='outline-primary' className='btn--rounded-padded float-right'>
              Edit
            </Button>
            <h3>{data.serverAssignments.server.name}</h3>
          </div>
          <DeliverySeparator className='delivery-assignments-separator' />
          {this.renderAssignments()}
        </div>
      );
    }
  }

  render() {
    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            <Layout.TopBar.Title>Fulfillment Stations</Layout.TopBar.Title>
          </Layout.TopBar.Left>
        </Layout.TopBar>
        <LayoutContent error={this.props.error}>
          <div>{this.renderContent()}</div>
        </LayoutContent>
      </Layout>
    );
  }
}

const query = gql`
  query CurrentAssignmentsQuery($venueId: ID!, $fulfillmentCenterId: ID!, $serverId: ID!) {
    fulfillmentCenter(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId) {
      id
      name
    }
    serverAssignments(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId, serverId: $serverId) {
      ...ServerAssignmentFields
    }
  }
  ${serverAssignmentsFragment}
`;

export const CurrentAssignments = (props: RouterProps) => {
  const { venueId, fulfillmentCenterId, serverId } = props.match.params;

  return (
    <Query<CurrentAssignmentsQuery, CurrentAssignmentsQueryVariables>
      variables={{ venueId, fulfillmentCenterId, serverId }}
      query={query}
      fetchPolicy='no-cache'
    >
      {({ loading, error, data }) => {
        return (
          <CurrentAssignmentsUnwrapped
            {...props}
            loading={loading}
            error={error}
            data={data}
            venueId={venueId}
            fulfillmentCenterId={fulfillmentCenterId}
          />
        );
      }}
    </Query>
  );
};
